<template>
  <div>
    <div ref="viewer" :class="{ 'markdown-truncated': !showFullText && isLongContent }" />
    <button
      v-if="isLongContent"
      class="mt-2 text-sm font-medium text-primary-purple-600 hover:text-primary-purple-700 hover:underline"
      @click="toggleText"
    >
      {{ showFullText ? 'Show less' : 'Show more' }}
    </button>
  </div>
</template>

<script>
import Editor from '@toast-ui/editor'
import '@toast-ui/editor/dist/toastui-editor-viewer.css'

export default {
  name: 'PostMarkdown',
  props: {
    text: {
      type: String,
      required: true,
    },
    maxLines: {
      type: Number,
      default: 15,
    },
  },
  data() {
    return {
      viewer: null,
      showFullText: false,
      isLongContent: false,
      resizeTimer: null,
      resizeObserver: null,
    }
  },
  watch: {
    text: {
      handler(newText) {
        this.showFullText = false
        this.isLongContent = false

        if (this.viewer) {
          this.viewer.setMarkdown(newText || '')
        }

        this.$nextTick(() => {
          this.checkContentLength()
        })
      },
      immediate: true,
    },
    showFullText() {
      this.$nextTick(() => {
        this.applyTruncationStyles()
      })
    },
    maxLines() {
      this.$nextTick(() => {
        this.checkContentLength()
      })
    },
  },
  mounted() {
    this.initViewer()
    const rootEl = this.$refs.viewer
    if (rootEl && typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => {
        this.handleResize()
      })
      this.resizeObserver.observe(rootEl)
    }
  },
  beforeUnmount() {
    if (this.viewer) {
      this.viewer.destroy()
    }
    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
      this.resizeObserver = null
    }
    if (this.resizeTimer) {
      clearTimeout(this.resizeTimer)
      this.resizeTimer = null
    }
  },
  methods: {
    handleResize() {
      if (this.resizeTimer) clearTimeout(this.resizeTimer)
      this.resizeTimer = setTimeout(() => {
        this.checkContentLength()
      }, 150)
    },
    initViewer() {
      this.viewer = Editor.factory({
        el: this.$refs.viewer,
        initialValue: this.text || '',
        usageStatistics: false,
        viewer: true,
      })
      this.$nextTick(() => {
        this.checkContentLength()
      })
    },
    checkContentLength() {
      const contentElement = this.$refs.viewer?.querySelector('.toastui-editor-contents')
      if (!contentElement) return

      const originalScrollHeight = contentElement.scrollHeight

      Object.assign(contentElement.style, {
        display: '-webkit-box',
        WebkitLineClamp: this.maxLines.toString(),
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
      })

      const clampedClientHeight = contentElement.clientHeight
      const clampedScrollHeight = contentElement.scrollHeight
      this.isLongContent =
        originalScrollHeight > clampedClientHeight || clampedScrollHeight > clampedClientHeight
      Object.assign(contentElement.style, {
        display: '',
        WebkitLineClamp: '',
        WebkitBoxOrient: '',
        overflow: '',
      })

      this.applyTruncationStyles()
    },
    applyTruncationStyles() {
      const contentElement = this.$refs.viewer?.querySelector('.toastui-editor-contents')
      if (!contentElement) return

      const shouldTruncate = !this.showFullText && this.isLongContent

      Object.assign(contentElement.style, {
        display: shouldTruncate ? '-webkit-box' : '',
        WebkitLineClamp: shouldTruncate ? this.maxLines.toString() : '',
        WebkitBoxOrient: shouldTruncate ? 'vertical' : '',
        overflow: shouldTruncate ? 'hidden' : '',
      })
    },
    toggleText() {
      this.showFullText = !this.showFullText
      this.applyTruncationStyles()
    },
  },
}
</script>
